import React, { useState, useEffect } from 'react';
import { FaFilePdf, FaHistory, FaClock, FaBook, FaUserGraduate, FaChartLine, FaSearch } from 'react-icons/fa';
import moment from 'moment-timezone';
import './TranscriptTable.css';
import { generateQRCode } from '../../../client/Components/SAT/generateQRCode';
import { generateCertificate } from '../../../client/Components/SAT/generateCertificate';

const TranscriptTable = ({ data = [], progressHistoryData = [], usersData = [] }) => {
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [combinedData, setCombinedData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    // Combine transcript data with progress history when component mounts
    const combined = data.map(transcript => {
      const matchingProgress = progressHistoryData.filter(
        progress => progress.transcriptEmail === transcript.email
      );
      return {
        ...transcript,
        progressHistory: [
          ...(transcript.progressHistory || []),
          ...matchingProgress.map(progress => ({
            testScore: progress.testScore,
            lastQuizCompleted: progress.lastQuizCompleted,
            lastTestDate: progress.lastTestDate,
            totalHours: progress.totalHours,
            createdAt: progress.createdAt,
            updatedAt: progress.updatedAt
          }))
        ]
      };
    });
    setCombinedData(combined);
  }, [data, progressHistoryData]);

  // Sort data by most recent update date
  const sortedData = [...combinedData].sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || 0);
    const dateB = new Date(b.updatedAt || b.createdAt || 0);
    return dateB - dateA;
  });

  // Filter data based on search term
  const filteredData = sortedData.filter(transcript => {
    if (!searchTerm) return true;
    const emailMatch = transcript.email.toLowerCase().includes(searchTerm.toLowerCase());
    const user = usersData.find(u => u.email === transcript.email);
    const nameMatch = user?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return emailMatch || nameMatch;
  });

  // Get the data to display - last 3 or filtered results
  const displayData = showAll || searchTerm ? filteredData : filteredData.slice(0, 3);

  const calculateTotalHours = () => {
    if (!selectedTranscript?.progressHistory?.length) return 'N/A';
    
    const total = selectedTranscript.progressHistory.reduce((sum, item) => {
      const hours = parseFloat(item.totalHours);
      return sum + (isNaN(hours) ? 0 : hours);
    }, 0);
    
    return typeof total === 'number' ? total.toFixed(2) : 'N/A';
  };

  const calculateAverageScore = () => {
    if (!selectedTranscript?.progressHistory?.length) return 'N/A';
    
    const validScores = selectedTranscript.progressHistory.filter(
      item => typeof item.testScore === 'number'
    );
    if (validScores.length === 0) return 'N/A';
    
    const average = validScores.reduce((sum, item) => sum + item.testScore, 0) / validScores.length;
    return Math.round(average);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return moment(date).tz('America/New_York').format('MMM D, YYYY h:mm A');
  };

  const getQuizResults = () => {
    if (!selectedTranscript?.progressHistory?.length) return [];
    return selectedTranscript.progressHistory
      .filter(item => item.lastQuizCompleted)
      .map(item => ({
        lastQuizCompleted: item.lastQuizCompleted,
        testScore: item.testScore,
        lastTestDate: item.lastTestDate,
        totalHours: item.totalHours
      }));
  };

  const handleGenerateTranscript = async () => {
    if (!selectedTranscript) return;

    try {
      // Find the user (case-insensitive match)
      const user = usersData.find(u => 
        u.email && selectedTranscript.email &&
        u.email.toLowerCase() === selectedTranscript.email.toLowerCase()
      );

      // Use name if available (even if "Anonymous"), otherwise use email
      const displayName = user?.name || selectedTranscript.email;

      // Calculate certificate data
      const highestScore = selectedTranscript.progressHistory.reduce((max, item) => {
        return Math.max(max, item.testScore || 0);
      }, 0);
      
      const totalHours = selectedTranscript.progressHistory.reduce((sum, item) => {
        return sum + (parseFloat(item.totalHours) || 0);
      }, 0);

      // Get date range
      const dates = selectedTranscript.progressHistory
        .map(item => new Date(item.lastTestDate))
        .filter(date => !isNaN(date.getTime()));
      
      const startDate = dates.length > 0 ? 
        new Date(Math.min(...dates)).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0];
      
      const completionDate = dates.length > 0 ? 
        new Date(Math.max(...dates)).toISOString().split('T')[0] : 
        new Date().toISOString().split('T')[0];

      // Generate QR code
      const transcriptUrl = `${window.location.origin}/transcript?email=${encodeURIComponent(selectedTranscript.email)}`;
      const qrCodeImage = await generateQRCode(transcriptUrl);

      // Generate certificate
      await generateCertificate({
        name: displayName,
        startDate,
        completionDate,
        score: highestScore,
        hours: totalHours,
        qrCodeImage,
        updateStatusMessage: (msg) => console.log(msg)
      });
      
    } catch (error) {
      console.error("Error generating certificate:", error);
    }
  };

  if (!selectedTranscript) {
    return (
      <div className="transcript-list-view">
        <h3><FaFilePdf /> Student Transcripts</h3>
        
        {/* Search Bar */}
        <div className="search-container" style={{ marginBottom: '1.5rem', maxWidth: '500px' }}>
          <div style={{ position: 'relative' }}>
            <FaSearch style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#a0a0a0'
            }} />
            <input
              type="text"
              placeholder="Search by email or name..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setShowAll(true);
              }}
              style={{
                padding: '0.75rem 1rem 0.75rem 40px',
                borderRadius: '8px',
                border: '1px solid #4a4f56',
                background: '#3a3f46',
                color: '#e0e0e0',
                width: '100%'
              }}
            />
          </div>
        </div>

        {/* User Cards */}
        <div className="transcript-grid">
          {displayData.map(transcript => {
            const user = usersData.find(u => u.email === transcript.email);
            return (
              <div 
                key={transcript.email}
                className="transcript-card"
                onClick={() => setSelectedTranscript(transcript)}
              >
                
                {user?.name && (
                  <div className="transcript-name" style={{ color: '#c5bfbfff', marginBottom: '0.5rem' }}>
                    {user.name}
                  </div>
                )}
                
                <div className="transcript-meta">
                  <span><FaHistory style={{ color: 'red' }} /> {transcript.progressHistory?.length || 0} records</span>
                  <span><FaChartLine style={{ color: 'limegreen' }} /> Last score: {
                    transcript.progressHistory?.[0]?.testScore || 'N/A'
                  }</span>
                </div>
                <div style={{ color: '#a0a0a0', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                  Last updated: {formatDate(transcript.updatedAt || transcript.createdAt)}
                </div>
                <div className="transcript-email">{transcript.email}</div>
              </div>
            );
          })}
        </div>

        {/* Show All/Show Less button when not searching */}
        {!searchTerm && (
          <button
            onClick={() => setShowAll(!showAll)}
            style={{
              background: 'none',
              border: 'none',
              color: '#4dabf7',
              cursor: 'pointer',
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            {showAll ? 'Show Less' : 'Show All'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="transcript-detail-view">
      <button 
        className="back-button"
        onClick={() => setSelectedTranscript(null)}
      >
        ← Back to List
      </button>

      <div className="transcript-header">
        <h3><FaUserGraduate /> {selectedTranscript.email}</h3>
        <div className="tabs">
          <button 
            className={activeTab === 'summary' ? 'active' : ''}
            onClick={() => setActiveTab('summary')}
          >
            Summary
          </button>
          <button 
            className={activeTab === 'history' ? 'active' : ''}
            onClick={() => setActiveTab('history')}
          >
            Progress History
          </button>
          <button 
            className={activeTab === 'quizzes' ? 'active' : ''}
            onClick={() => setActiveTab('quizzes')}
          >
            Quiz Results
          </button>
        </div>
      </div>

      {activeTab === 'summary' && (
        <div className="summary-view">
          <div className="stats-card">
            <h4>Total Study Hours</h4>
            <div className="stat-value">
              {calculateTotalHours()}
            </div>
          </div>
          <div className="stats-card">
            <h4>Average Score</h4>
            <div className="stat-value">
              {calculateAverageScore()}
            </div>
          </div>
          <div 
            className="stats-card clickable" 
            onClick={handleGenerateTranscript}
            title="Generate and download transcript"
          >
            <h4>Certificate</h4>
            <div className="stat-value">
              <FaFilePdf />
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="history-view">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Quiz</th>
                <th>Score</th>
                <th>Hours</th>
              </tr>
            </thead>
            <tbody>
              {selectedTranscript.progressHistory?.length > 0 ? (
                selectedTranscript.progressHistory.map((item, index) => (
                  <tr key={index}>
                    <td>{formatDate(item.lastTestDate)}</td>
                    <td>{item.lastQuizCompleted || 'N/A'}</td>
                    <td className={`score-${Math.floor((item.testScore || 0)/10)*10}`}>
                      {item.testScore || 'N/A'}
                    </td>
                    <td>{item.totalHours ? parseFloat(item.totalHours).toFixed(2) : 'N/A'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">No progress history available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'quizzes' && (
        <div className="quizzes-view">
          <div className="quiz-stats">
            <div className="stat-card">
              <FaBook className="stat-icon" />
              <div className="stat-value">{getQuizResults().length}</div>
              <div className="stat-label">Total Quizzes</div>
            </div>
          </div>
          
          <div className="quiz-list">
            {getQuizResults().length > 0 ? (
              getQuizResults().map((quiz, index) => (
                <div key={index} className="quiz-card">
                  <div className="quiz-header">
                    <div className="quiz-name">{quiz.lastQuizCompleted || 'N/A'}</div>
                    <div className={`quiz-score score-${Math.floor((quiz.testScore || 0)/10)*10}`}>
                      {quiz.testScore || 'N/A'}
                    </div>
                  </div>
                  <div className="quiz-details">
                    <div className="detail-item">
                      <FaClock className="detail-icon" />
                      <span>{formatDate(quiz.lastTestDate)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Hours:</span>
                      <span>{quiz.totalHours ? parseFloat(quiz.totalHours).toFixed(2) : 'N/A'}</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-quizzes">No quiz results available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TranscriptTable;